import {commands} from "../ApiUtils";
import IStack from "../util/collection/IStack";
import SimpleStack from "../util/collection/SimpleStack";

export type EditorCommandArgs = {
    point1: Point | undefined,
    point2: Point | undefined,
    arg1: number | undefined,
    arg2: number | undefined,
    arg3: number | undefined,
    arg4: number | undefined
}

export class EditorCommand {
    typeCode: string = ""
    args: EditorCommandArgs | undefined
}

export type Point = {
    x: number,
    y: number
}

export enum EditorModeType {
    ROUTES,
    CUT,
    DRILL,
    G_CODE_ZERO
}

export type PolyLine = {
    points: Array<Point>,
    width: number
    layerType: EditorModeType
}

export type DrillPoint = {
    point: Point,
    diameter: number
}

export type PaintData = {
    routes: Array<PolyLine>,
    cuts: Array<PolyLine>,
    holes: Array<DrillPoint>
}

export class Editor {
    private paintingLineIndex: number = 0
    private paintingLinePointsCount: number = 0
    private routesData: Array<PolyLine> = []
    private drillData: Array<DrillPoint> = []
    private initialZeroOffset: Point | null = null
    private zeroOffset: Point | null = null

    private lastStartLineCommand: EditorCommand | null = null

    private stack: IStack<EditorCommand> = new SimpleStack()
    private cancelStack: IStack<EditorCommand> = new SimpleStack()

    paintData(): PaintData {
        return {
            routes: this.routesData.filter(c => c.layerType == EditorModeType.ROUTES),
            cuts: this.routesData.filter(c => c.layerType == EditorModeType.CUT),
            holes: this.drillData
        }
    }

    setInitialZeroOffset(point: Point) {
        this.initialZeroOffset = {
            x: point.x,
            y: point.y
        }
        this.zeroOffset = {
            x: point.x,
            y: point.y
        }
    }

    getZeroOffset(): Point | null {
        return this.zeroOffset
    }

    clear() {
        this.stack.clear()
        this.cancelStack.clear()
        this.clearPaintData()
        this.lastStartLineCommand = null
        this.initialZeroOffset = null
        this.zeroOffset = null
    }

    resetForReplay() {
        this.stack.clear()
        this.cancelStack.clear()
        this.clearPaintData()
        this.lastStartLineCommand = null
        this.zeroOffset = this.initialZeroOffset == null ? null : {
            x: this.initialZeroOffset.x,
            y: this.initialZeroOffset.y
        }
    }

    applyAllCommands(commands: Array<EditorCommand>) {
        commands.forEach(c => this.applyCommand(c))
    }

    applyCommand(command: EditorCommand) {
        if (command.typeCode === commands.startLine.typeCode) {
            this.lastStartLineCommand = command
            return;
        }

        if (command.typeCode === commands.cancel.typeCode) {
            this.cancelS(this.stack, this.cancelStack)
            return
        }
        if (command.typeCode === commands.unCancel.typeCode) {
            this.uncancelS(this.stack, this.cancelStack)
            return
        }

        if (command.typeCode === commands.lineTo.typeCode && this.lastStartLineCommand != null) {
            if (this.lastStartLineCommand.args != null) {
                if (command.args?.point1 != null) {
                    this.lastStartLineCommand.args.point2 = {
                        x: command.args.point1.x,
                        y: command.args.point1.y
                    }
                }
            }
            this.pushS(this.stack, this.cancelStack, this.lastStartLineCommand)
            this.lastStartLineCommand = null
        } else if (command.typeCode === commands.finishLine.typeCode) {
            if (this.lastStartLineCommand != null) {
                this.lastStartLineCommand = null
            }
        } else {
            this.pushS(this.stack, this.cancelStack, command)
        }
    }

    processCommands() {
        this.clearPaintData()
        this.zeroOffset = this.initialZeroOffset == null ? null : {
            x: this.initialZeroOffset.x,
            y: this.initialZeroOffset.y
        }
        this.stack.toArray().forEach(c => this.processCommand(c))
        if (this.lastStartLineCommand != null) {
            this.processCommand(this.lastStartLineCommand)
        }
    }

    private cancelS<T>(stack: IStack<T>, cancelStack: IStack<T>) {
        if (stack.size() > 0) {
            cancelStack.push(stack.pop())
        }
    }

    private uncancelS<T>(stack: IStack<T>, cancelStack: IStack<T>) {
        if (cancelStack.size() > 0) {
            stack.push(cancelStack.pop())
        }
    }

    private pushS<T>(stack: IStack<T>, cancelStack: IStack<T>, value: T) {
        cancelStack.clear()
        stack.push(value)
    }

    private clearPaintData() {
        this.drillData = []
        this.routesData = []
        this.paintingLineIndex = 0
        this.paintingLinePointsCount = 0
    }

    private processCommand(command: EditorCommand) {
        if (command.typeCode === commands.startLine.typeCode) {
            this.startLine(command.args?.point1!, command.args?.point2, command.args?.arg1!, command.args?.arg2!)
        }
        if (command.typeCode === commands.lineTo.typeCode) {
            this.continueLine(command.args?.point1!)
        }
        if (command.typeCode === commands.finishLine.typeCode) {
            this.finishLine()
        }
        if (command.typeCode === commands.addHole.typeCode) {
            this.drillHole(command.args?.point1!, command.args?.arg1!)
        }
        if (command.typeCode === commands.setZero.typeCode) {
            this.setZero(command.args?.point1!)
        }
    }

    private startLine(point1: Point, point2: Point | undefined, toolDiameter: number, type: EditorModeType) {
        this.paintingLineIndex = this.routesData.length
        this.routesData.push({
            points: [{
                x: point1.x,
                y: point1.y
            }],
            width: toolDiameter,
            layerType: type
        })
        this.paintingLinePointsCount = 1
        if (point2 != null) {
            this.continueLine(point2)
        }
    }

    private continueLine(point: Point) {
        this.routesData[this.paintingLineIndex].points.push({
            x: point.x,
            y: point.y
        })
        this.paintingLinePointsCount++
    }

    private finishLine() {
        if (this.paintingLinePointsCount === 1) {
            this.routesData.splice(this.paintingLineIndex, 1)
        }
    }

    private drillHole(point: Point, diameter: number) {
        this.drillData.push({
            point: point,
            diameter: diameter
        })
    }

    private setZero(point: Point) {
        this.zeroOffset = {
            x: point.x,
            y: point.y
        }
    }
}
